import { useState } from 'react';

interface OnboardingWizardProps {
  onComplete: () => void;
  onSkip: () => void;
}

export function OnboardingWizard({ onComplete, onSkip }: OnboardingWizardProps) {
  const [currentStep, setCurrentStep] = useState(0);

  const steps = [
    {
      title: "Welcome to WaySightAI",
      description: "Privacy-first time series forecasting",
      content: (
        <div className="text-center space-y-6">
          <div className="w-20 h-20 mx-auto bg-gradient-to-br from-blue-500 to-indigo-600 rounded-2xl flex items-center justify-center shadow-lg shadow-blue-500/10">
            <span className="text-4xl text-white">📊</span>
          </div>
          <h2 className="text-2xl font-bold text-gray-900">Privacy-First Edge Analytics</h2>
          <p className="text-sm text-gray-600 max-w-md mx-auto leading-relaxed">
            WaySightAI builds forecasts directly inside your browser using WebAssembly. 
            Your dataset is parsed and computed locally—meaning zero raw data ever leaves your computer.
          </p>
          <div className="flex items-center justify-center space-x-2 text-xs font-semibold text-emerald-700 bg-emerald-50 px-3 py-1.5 rounded-full border border-emerald-100 max-w-max mx-auto">
            <svg className="w-4 h-4 text-emerald-500" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M2.166 4.999A11.954 11.954 0 0010 1.944 11.954 11.954 0 0017.834 5c.11.65.166 1.32.166 2.001 0 5.225-3.34 9.67-8 11.317C5.34 16.67 2 12.225 2 7c0-.682.057-1.35.166-2.001zm11.541 3.708a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
            </svg>
            <span>100% Secure & Compliant</span>
          </div>
        </div>
      ),
    },
    {
      title: "Simple 3-Step Process",
      description: "From data to insights in seconds",
      content: (
        <div className="space-y-4">
          <h2 className="text-lg font-bold text-gray-900 text-center mb-2">How it works</h2>
          <div className="space-y-3">
            <StepCard
              number={1}
              title="Upload Your Data"
              description="Drop a CSV file containing date and numeric columns."
              icon="📁"
            />
            <StepCard
              number={2}
              title="Map & Configure"
              description="Choose date/target columns and customize your model."
              icon="⚙️"
            />
            <StepCard
              number={3}
              title="Generate & Export"
              description="Review predictions, residual diagnostics, and export summaries."
              icon="📈"
            />
          </div>
        </div>
      ),
    },
    {
      title: "Methods & Quality Metrics",
      description: "What you get with our WASM engine",
      content: (
        <div className="space-y-4">
          <h2 className="text-sm font-bold text-gray-900 text-center mb-1">Supported Analytics</h2>
          <div className="grid grid-cols-1 gap-2.5">
            <div className="p-2.5 bg-slate-50 border border-gray-200/60 rounded-xl flex items-start space-x-2.5">
              <span className="text-base">🤖</span>
              <div>
                <h4 className="font-bold text-[11px] text-gray-900">Forecasting Models</h4>
                <p className="text-[10px] text-gray-500">Auto-tuned ARIMA and Triple Exponential Smoothing (Holt-Winters).</p>
              </div>
            </div>
            <div className="p-2.5 bg-slate-50 border border-gray-200/60 rounded-xl flex items-start space-x-2.5">
              <span className="text-base">📊</span>
              <div>
                <h4 className="font-bold text-[11px] text-gray-900">Statistical Diagnostics</h4>
                <p className="text-[10px] text-gray-500">Augmented Dickey-Fuller (ADF) & KPSS stationarity tests, plus ACF/PACF residual plots.</p>
              </div>
            </div>
            <div className="p-2.5 bg-slate-50 border border-gray-200/60 rounded-xl flex items-start space-x-2.5">
              <span className="text-base">🛡️</span>
              <div>
                <h4 className="font-bold text-[11px] text-gray-900">Error & Quality Metrics</h4>
                <p className="text-[10px] text-gray-500">Direct calculation of MAPE, RMSE, MAE, AIC, and BIC to validate performance.</p>
              </div>
            </div>
          </div>
        </div>
      ),
    },
  ];

  const handleNext = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      onComplete();
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  return (
    <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-xl max-w-md w-full overflow-hidden border border-gray-100">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-indigo-655 text-white p-5">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-lg font-bold">{steps[currentStep].title}</h1>
              <p className="text-blue-100 text-xs mt-0.5">{steps[currentStep].description}</p>
            </div>
            <button
              onClick={onSkip}
              className="text-white hover:text-blue-100 text-xs font-semibold underline underline-offset-2"
            >
              Skip
            </button>
          </div>
        </div>

        {/* Progress bar */}
        <div className="bg-gray-100 h-1">
          <div
            className="bg-blue-650 h-1 transition-all duration-300"
            style={{ width: `${((currentStep + 1) / steps.length) * 100}%` }}
          />
        </div>

        {/* Content */}
        <div className="p-6 min-h-[300px] flex flex-col justify-center">
          {steps[currentStep].content}
        </div>

        {/* Footer */}
        <div className="border-t border-gray-100 p-4 bg-gray-50 flex items-center justify-between">
          <button
            onClick={handleBack}
            disabled={currentStep === 0}
            className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors ${
              currentStep === 0
                ? 'text-gray-305 cursor-not-allowed'
                : 'text-gray-700 hover:bg-gray-200 bg-white border border-gray-250/70'
            }`}
          >
            Back
          </button>
          <div className="flex items-center space-x-1.5">
            {steps.map((_, index) => (
              <div
                key={index}
                className={`w-1.5 h-1.5 rounded-full transition-all ${
                  index === currentStep
                    ? 'bg-blue-600 w-4'
                    : 'bg-gray-300'
                }`}
              />
            ))}
          </div>
          <button
            onClick={handleNext}
            className="px-4 py-1.5 bg-blue-600 text-white rounded-lg text-xs font-semibold hover:bg-blue-700 shadow-sm transition-all"
          >
            {currentStep === steps.length - 1 ? "Let's Start!" : 'Next'}
          </button>
        </div>
      </div>
    </div>
  );
}

interface StepCardProps {
  number: number;
  title: string;
  description: string;
  icon: string;
}

function StepCard({ number, title, description, icon }: StepCardProps) {
  return (
    <div className="flex items-start space-x-3 p-3 bg-white rounded-xl border border-gray-200/60 shadow-sm">
      <div className="flex-shrink-0">
        <div className="w-8 h-8 bg-blue-50 text-blue-650 rounded-lg flex items-center justify-center text-sm font-bold border border-blue-100/50">
          {number}
        </div>
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center space-x-1.5 mb-0.5">
          <span className="text-base leading-none">{icon}</span>
          <h3 className="font-bold text-gray-900 text-xs sm:text-sm">{title}</h3>
        </div>
        <p className="text-[11px] sm:text-xs text-gray-500 leading-normal">{description}</p>
      </div>
    </div>
  );
}
